import { useState } from "react";
import type { ReactNode } from "react";
import { Eye, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  formatFileSize,
  resolveMediaUrl,
} from "@/features/documents/lib/resolve-media-url";
import { getRoleDisplayName } from "@/lib/auth-utils";
import type { UserRole } from "@/types/user";
import type { Document } from "@/types/document";
import { formatOptionalArabicDateTime } from "@/lib/formatters";

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border last:border-0">
      <span className="text-sm font-medium text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground text-left">{children}</span>
    </div>
  );
}

/** The `permissions` JSON is app-defined; we use an `allowedRoles: string[]` convention. */
function readAllowedRoles(document: Document): string[] {
  const raw = (document.permissions as Record<string, unknown> | undefined)
    ?.allowedRoles;
  return Array.isArray(raw)
    ? raw.filter((r): r is string => typeof r === "string")
    : [];
}

interface DocumentDetailDialogProps {
  document: Document;
  departmentName?: string;
  libraryName?: string;
}

export function DocumentDetailDialog({
  document,
  departmentName,
  libraryName,
}: DocumentDetailDialogProps) {
  const [open, setOpen] = useState(false);
  const href = resolveMediaUrl(document.url);
  const allowedRoles = readAllowedRoles(document);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
          aria-label="عرض تفاصيل المستند"
          title="عرض"
        >
          <Eye className="w-4 h-4" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader className="text-right">
          <DialogTitle>{document.name}</DialogTitle>
        </DialogHeader>

        <div className="py-2">
          <Row label="الاسم الأصلي">{document.originalName || "—"}</Row>
          <Row label="النوع">{document.mimeType || "—"}</Row>
          <Row label="الحجم">{formatFileSize(Number(document.sizeBytes))}</Row>
          <Row label="الدائرة">
            {departmentName ?? document.departmentId ?? "—"}
          </Row>
          <Row label="المكتبة">{libraryName ?? document.libraryId ?? "—"}</Row>
          <Row label="تاريخ الرفع">
            {formatOptionalArabicDateTime(document.createdAt)}
          </Row>
          <Row label="آخر تحديث">
            {formatOptionalArabicDateTime(document.updatedAt)}
          </Row>
          <Row label="الأدوار المصرح لها">
            {allowedRoles.length ? (
              <span className="flex flex-wrap justify-end gap-1">
                {allowedRoles.map((role) => (
                  <Badge key={role} variant="secondary">
                    {getRoleDisplayName(role as UserRole)}
                  </Badge>
                ))}
              </span>
            ) : (
              "الجميع"
            )}
          </Row>
        </div>

        <DialogFooter>
          {href && (
            <a href={href} target="_blank" rel="noopener noreferrer">
              <Button variant="default">
                <ExternalLink className="w-4 h-4 ml-2" />
                فتح الملف
              </Button>
            </a>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
